import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import ClickDetailsModal from './ClickDetailsModal';
import { formatUtcDateTime, utcDateKey, utcDateKeyDaysAgo } from '../dateTime';

const formatCount = (value) => Number(value || 0).toLocaleString('uk-UA');

const formatTime = (value) => formatUtcDateTime(value, { includeYear: true });

const INTERVALS = ['до 10 сек', '10–60 сек', '1–5 хв', '5–30 хв', '30–60 хв', '1–24 год', 'понад добу'];

const shiftDays = (days) => utcDateKeyDaysAgo(days);

const today = () => utcDateKey();

export default function RepeatsPanel({ onError }) {
  const [filters, setFilters] = useState({
    startDate: shiftDays(6),
    endDate: today(),
    appId: '',
    deviceID: '',
  });
  const [applied, setApplied] = useState(filters);
  const [summary, setSummary] = useState(null);
  const [devices, setDevices] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const params = useCallback(
    (extra) => ({
      startDate: `${applied.startDate}T00:00:00.000Z`,
      endDate: `${applied.endDate}T23:59:59.999Z`,
      appId: applied.appId.trim() || undefined,
      ...extra,
    }),
    [applied]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.all([
      axios.get('/api/clicks/repeats/summary', { params: params() }),
      axios.get('/api/clicks/repeats/devices', {
        params: params({ deviceID: applied.deviceID.trim() || undefined, page, pageSize: 50 }),
      }),
    ])
      .then(([summaryRes, devicesRes]) => {
        if (cancelled) return;
        setSummary(summaryRes.data);
        setDevices(devicesRes.data?.items || []);
        setCount(devicesRes.data?.count || 0);
        setTotalPages(devicesRes.data?.totalPages || 1);
      })
      .catch((err) => {
        if (!cancelled) onError?.(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [applied, page, params, onError]);

  const search = () => {
    setPage(1);
    setApplied(filters);
  };

  const patch = (changes) => setFilters({ ...filters, ...changes });

  const maxGap = Math.max(...(summary?.intervals || []).map((row) => row.gaps), 0);

  return (
    <>
      <div className="filters-card app-editor-card compact-card mt-3">
        <div className="filters-bar">
          <div className="filters-stats">
            <div className="fstat">
              <span className="fstat-label">Повторів</span>
              <span className="fstat-value">{formatCount(summary?.repeats)}</span>
            </div>
            <div className="fstat">
              <span className="fstat-label">Пристроїв</span>
              <span className="fstat-value">{formatCount(summary?.devices)}</span>
            </div>
            <div className="fstat">
              <span className="fstat-label">У списку</span>
              <span className="fstat-value">{formatCount(count)}</span>
            </div>
          </div>

          <div className="filters-actions">
            <button type="button" className="fbtn is-primary" onClick={search} disabled={loading}>
              {loading ? 'Оновлення…' : 'Оновити'}
            </button>
          </div>
        </div>

        <div className="filters-grid">
          <div className="f-field">
            <span className="f-label">Від</span>
            <div className="f-control">
              <input
                type="date"
                className="f-input"
                value={filters.startDate}
                max={filters.endDate}
                onChange={(e) => patch({ startDate: e.target.value })}
              />
            </div>
          </div>

          <div className="f-field">
            <span className="f-label">До</span>
            <div className="f-control">
              <input
                type="date"
                className="f-input"
                value={filters.endDate}
                min={filters.startDate}
                onChange={(e) => patch({ endDate: e.target.value })}
              />
            </div>
          </div>

          <div className="f-field f-span-2">
            <span className="f-label">App ID</span>
            <div className="f-control">
              <input
                className="f-input"
                placeholder="усі додатки"
                value={filters.appId}
                onChange={(e) => patch({ appId: e.target.value })}
              />
            </div>
          </div>

          <div className="f-field f-span-2">
            <span className="f-label">Device ID</span>
            <div className="f-control">
              <input
                className="f-input"
                placeholder="пошук за пристроєм"
                value={filters.deviceID}
                onChange={(e) => patch({ deviceID: e.target.value })}
              />
            </div>
          </div>
        </div>
      </div>

      {summary ? (
        <div className="app-editor-card compact-card mt-3">
          <div className="app-editor-card-body compact-card-body rp-summary">
            <section className="rp-block">
              <div className="cd-group-title">Скільки разів заходили</div>
              <div className="reasons-chips">
                {(summary.distribution || []).map((row) => (
                  <span className="reason-chip" key={row.clicks}>
                    <span className="reason-chip-name">{row.clicks} кліки</span>
                    <span className="reason-chip-count">{formatCount(row.devices)}</span>
                  </span>
                ))}
              </div>
            </section>

            <section className="rp-block">
              <div className="cd-group-title">Інтервал між кліками</div>
              <div className="rp-list">
                {(summary.intervals || []).map((row) => (
                  <div className="rp-row is-gap" key={row.bucket}>
                    <span className="rp-row-name">{INTERVALS[row.bucket] || '—'}</span>
                    <span className="rp-bar">
                      <span
                        className="rp-bar-fill"
                        style={{ width: `${maxGap ? (row.gaps / maxGap) * 100 : 0}%` }}
                      />
                    </span>
                    <span className="rp-row-value">{formatCount(row.gaps)}</span>
                  </div>
                ))}
                {!summary.intervals?.length ? (
                  <div className="rp-row-empty">Недостатньо даних</div>
                ) : null}
              </div>
            </section>

            <section className="rp-block">
              <div className="cd-group-title">Найбільша частка повторів</div>
              <div className="rp-list">
                {(summary.apps || []).map((row) => (
                  <div className="rp-row" key={row.appId}>
                    <span className="rp-row-name" title={row.appId}>{row.appId}</span>
                    <span className="rp-row-sub" title="повторів із усіх кліків">
                      {formatCount(row.repeats)}/{formatCount(row.repeats + row.clicks)}
                    </span>
                    <span className="rp-row-value">{row.share}%</span>
                  </div>
                ))}
                {!summary.apps?.length ? (
                  <div className="rp-row-empty">Замало кліків для оцінки</div>
                ) : null}
              </div>
            </section>
          </div>
        </div>
      ) : null}

      <div className="app-editor-card compact-card mt-3">
        <div className="app-editor-card-body compact-card-body">
          <div className="apps-head">
            <div>
              <div className="stats-section-title">Пристрої з повторними кліками</div>
              <div className="stats-section-subtitle">
                Сторінка {page} з {totalPages}
              </div>
            </div>

            <div className="filters-actions">
              <button type="button" className="fbtn" onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={page <= 1}>
                Назад
              </button>
              <button
                type="button"
                className="fbtn"
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                disabled={page >= totalPages}
              >
                Далі
              </button>
            </div>
          </div>

          {!devices.length ? (
            <div className="stats-empty-state">
              <div className="stats-empty-title">Повторних кліків немає</div>
              <div className="stats-empty-text">За обраний період жоден пристрій не заходив двічі.</div>
            </div>
          ) : (
            <div className="rp-table-wrap">
              <table className="rp-table">
                <thead>
                  <tr>
                    <th>Device ID</th>
                    <th>App ID</th>
                    <th className="is-num">Кліків</th>
                    <th>Перший повтор (UTC)</th>
                    <th>Останній (UTC)</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((row) => (
                    <tr key={`${row.deviceID}|${row.appId}`} onClick={() => setSelected(row)}>
                      <td className="rp-mono">{row.deviceID}</td>
                      <td>{row.appId}</td>
                      <td className="is-num">
                        <span className="rp-badge">{row.clicks}</span>
                      </td>
                      <td>{formatTime(row.firstRepeat)}</td>
                      <td>{formatTime(row.lastRepeat)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ClickDetailsModal show={Boolean(selected)} onHide={() => setSelected(null)} click={selected} />
    </>
  );
}
